import ModelTypeConstants from 'constants/ModelTypeConstants';
import APIUtil from 'util/APIUtil';
import {isValidDate} from 'util/DateUtil';
import {JSONValue} from 'util/Types';

import {PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES} from '../ParameterConstants';
import {ParameterType} from '../ParameterTypes';

export interface ParameterTypeToTSType {
  [ParameterType.BIOENTITY_LINK]: Array<string> | string | null;
  [ParameterType.DATE]: string | null;
  [ParameterType.ENTRY_LINK]: Array<string> | string | null;
  [ParameterType.FOLDER_LINK]: string | null;
  [ParameterType.NUMBER]: number | null;
  [ParameterType.PROCESS_LINK]: Array<string> | string | null;
  [ParameterType.PROCESS_TASK_LINK]: Array<string> | string | null;
  [ParameterType.PROJECT_LINK]: string | null;
  [ParameterType.SELECTOR]: Array<string> | string | null;
  [ParameterType.STORABLE_LINK]: string | null;
  [ParameterType.STUDY_LINK]: string | null;
  [ParameterType.STRING]: string | null;
  [ParameterType.TEAM_LINK]: Array<string> | string | null;
  [ParameterType.USER_LINK]: Array<string> | string | null;
}

function validateMultiselectableType(
  parameterType: ParameterType,
  valueJson: JSONValue,
  validatorFn: (valueJson: JSONValue) => boolean
): boolean {
  if (PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[parameterType] && Array.isArray(valueJson)) {
    return valueJson.every((value) => validatorFn(value));
  }

  return validatorFn(valueJson);
}

export const VALUE_VALIDATORS: {
  [k in ParameterType]: (valueJson: JSONValue) => valueJson is ParameterTypeToTSType[k];
} = {
  [ParameterType.DATE]: (valueJson: JSONValue): valueJson is ParameterTypeToTSType[ParameterType.DATE] =>
    (typeof valueJson === 'string' && isValidDate({textValue: valueJson})) || valueJson == null,
  [ParameterType.BIOENTITY_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.BIOENTITY_LINK] =>
    validateMultiselectableType(
      ParameterType.BIOENTITY_LINK,
      valueJson,
      (value) => (typeof value === 'string' && APIUtil.isBiologicalEntity(value)) || value == null
    ),
  [ParameterType.ENTRY_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.ENTRY_LINK] =>
    validateMultiselectableType(
      ParameterType.ENTRY_LINK,
      valueJson,
      (valueJson) =>
        (typeof valueJson === 'string' && APIUtil.apiIdToMaybeType(valueJson) === ModelTypeConstants.ENTRY) ||
        valueJson == null
    ),
  [ParameterType.FOLDER_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.FOLDER_LINK] =>
    (typeof valueJson === 'string' && APIUtil.apiIdToMaybeType(valueJson) === ModelTypeConstants.FOLDER) ||
    valueJson == null,
  [ParameterType.NUMBER]: (valueJson: JSONValue): valueJson is ParameterTypeToTSType[ParameterType.NUMBER] =>
    typeof valueJson === 'number' || valueJson == null,
  [ParameterType.PROCESS_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.PROCESS_LINK] =>
    validateMultiselectableType(
      ParameterType.PROCESS_LINK,
      valueJson,
      (valueJson) => (typeof valueJson === 'string' && APIUtil.isProcess(valueJson)) || valueJson == null
    ),
  [ParameterType.PROCESS_TASK_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.PROCESS_TASK_LINK] =>
    validateMultiselectableType(
      ParameterType.PROCESS_TASK_LINK,
      valueJson,
      (valueJson) => (typeof valueJson === 'string' && APIUtil.isProcessTask(valueJson)) || valueJson == null
    ),
  [ParameterType.PROJECT_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.PROJECT_LINK] =>
    (typeof valueJson === 'string' && APIUtil.isSource(valueJson)) || valueJson == null,
  [ParameterType.SELECTOR]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.SELECTOR] =>
    validateMultiselectableType(
      ParameterType.SELECTOR,
      valueJson,
      (valueJson) =>
        (typeof valueJson === 'string' &&
          APIUtil.apiIdToMaybeType(valueJson) === ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION) ||
        valueJson == null
    ),
  [ParameterType.STORABLE_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.STORABLE_LINK] =>
    validateMultiselectableType(
      ParameterType.STORABLE_LINK,
      valueJson,
      (valueJson) => (typeof valueJson === 'string' && APIUtil.isStorable(valueJson)) || valueJson == null
    ),
  [ParameterType.STRING]: (valueJson: JSONValue): valueJson is ParameterTypeToTSType[ParameterType.STRING] =>
    typeof valueJson === 'string' || valueJson == null,
  [ParameterType.STUDY_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.STUDY_LINK] =>
    validateMultiselectableType(
      ParameterType.STUDY_LINK,
      valueJson,
      (valueJson) => (typeof valueJson === 'string' && APIUtil.isStudy(valueJson)) || valueJson == null
    ),
  [ParameterType.TEAM_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.TEAM_LINK] =>
    validateMultiselectableType(
      ParameterType.TEAM_LINK,
      valueJson,
      (value) =>
        (typeof value === 'string' && APIUtil.apiIdToMaybeType(value) === ModelTypeConstants.TEAM) ||
        value == null
    ),
  [ParameterType.USER_LINK]: (
    valueJson: JSONValue
  ): valueJson is ParameterTypeToTSType[ParameterType.USER_LINK] =>
    validateMultiselectableType(
      ParameterType.USER_LINK,
      valueJson,
      (value) =>
        (typeof value === 'string' && APIUtil.apiIdToMaybeType(value) === ModelTypeConstants.ENTITY) ||
        value == null
    ),
};
